/**
 * SharePoint site and list configuration for the Harmony QMS admin dashboard.
 *
 * All QMS registers live on the Quality and Safety site
 * (https://harmonyhc1.sharepoint.com/sites/QualityandSafety). The dashboard
 * reads via client-side delegated Graph (the signed-in user's permissions),
 * mirroring the harmony-ap data-access posture (04-CONTEXT.md §3).
 *
 * NOTE (file-ownership): this file holds the site + list TITLES only. The
 * per-list field maps, the REGISTERS config, and the typed services are the
 * data layer's job in plan 04-02. Do NOT add field maps here.
 */

/**
 * SharePoint site URL in Graph API format (hostname:/path).
 * The Quality and Safety site that hosts every QMS register.
 */
export const SHAREPOINT_SITE_URL =
  'harmonyhc1.sharepoint.com:/sites/QualityandSafety';

/** Human-readable Q&S site URL (for links / display). */
export const SHAREPOINT_SITE_DISPLAY_URL =
  'https://harmonyhc1.sharepoint.com/sites/QualityandSafety';

/**
 * Graph composite site ID for the Quality and Safety site.
 * Used where a direct site-id reference is needed instead of resolving by URL.
 * (Resolution by SHAREPOINT_SITE_URL is the primary path in graphClient.ts.)
 */
export const SHAREPOINT_SITE_ID =
  'harmonyhc1.sharepoint.com,a5b9b844-d8c4-4660-8ef7-d4c6db328a8a';

/**
 * Display titles for the QMS register parent lists (the spine the dashboard
 * reads). These are the exact SharePoint list titles on the Q&S site
 * (01-register-conventions.md §1, 04-CONTEXT.md §4).
 */
export const REGISTER_LIST_NAMES = {
  incidents: 'Incidents',
  complaints: 'Complaints',
  continuousImprovement: 'Continuous Improvement',
  feedback: 'Feedback',
  risk: 'Risk Register',
} as const;

/**
 * Glass-box audit-log sibling list titles, one per register.
 * (NOT the legacy Centro mirrors "Incident Register Audit Log" /
 * "CI Register Audit Log" — the word *Register* distinguishes those;
 * 04-CONTEXT.md §4 ⚠️. Do not touch the legacy mirrors.)
 */
export const AUDIT_LIST_NAMES = {
  incidentsAudit: 'Incidents Audit Log',
  complaintsAudit: 'Complaints Audit Log',
  continuousImprovementAudit: 'Continuous Improvement Audit Log',
  feedbackAudit: 'Feedback Audit Log',
  riskAudit: 'Risk Register Audit Log',
} as const;

/**
 * Closure-loop list titles (Phase 2). `Closure Links` is the authoritative
 * many-to-many graph; `Care Plan Revisions` is the CPR anchor.
 */
export const CLOSURE_LIST_NAMES = {
  closureLinks: 'Closure Links',
  carePlanRevisions: 'Care Plan Revisions',
} as const;

/**
 * All QMS list titles the Graph client resolves IDs for, in one map.
 *
 * `useGraphClient` iterates this object to resolve a list ID per key on first
 * use (lists not yet provisioned resolve to '' and are skipped). Keeping the
 * shape `Record<key, title>` preserves the inherited useGraphClient contract.
 */
export const LIST_NAMES = {
  ...REGISTER_LIST_NAMES,
  ...AUDIT_LIST_NAMES,
  ...CLOSURE_LIST_NAMES,
} as const;

/** List name key type for type-safe list references */
export type ListNameKey = keyof typeof LIST_NAMES;

/** Human-facing reference prefixes per register (compose `<PREFIX>-<Id>`). */
export const REGISTER_PREFIXES = {
  incidents: 'INC',
  complaints: 'CMP',
  continuousImprovement: 'CI',
  feedback: 'FB',
  risk: 'RSK',
} as const;

/**
 * Build a Graph API URL for list items.
 *
 * @example
 * buildListItemsUrl('site-id-123', 'list-id-456')
 * // → '/sites/site-id-123/lists/list-id-456/items'
 */
export function buildListItemsUrl(siteId: string, listId: string): string {
  return `/sites/${siteId}/lists/${listId}/items`;
}

/**
 * Build a Graph API URL for a single list item.
 *
 * @example
 * buildListItemUrl('site-id-123', 'list-id-456', '7')
 * // → '/sites/site-id-123/lists/list-id-456/items/7'
 */
export function buildListItemUrl(
  siteId: string,
  listId: string,
  itemId: string,
): string {
  return `/sites/${siteId}/lists/${listId}/items/${itemId}`;
}
